import * as React from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { FormDryRunAlert } from "./formDryRunAlert";
import { MinusCircleOutlined } from "@ant-design/icons";
import { getDryRunErrorText } from "../../mk8s/getDryRunErrorText";
import { FormNGInput } from "./components/FormNGInput";
import { FormNGSelect } from "./components/FormNGSelect";
import { FormNGKindSelect } from "./components/FormNGKindSelect";
import { DomainInput } from "./types/input";
import { linksOf } from "../../../services/cpln";

interface Props {
  portId: string;
  portIndex: number;
  routeId: string;
}
export const CreateNGAdvancedPortRoute: React.FC<Props> = ({ portId, portIndex, routeId }) => {
  const { control, watch, setValue } = useFormContext<DomainInput>();

  const { remove: removeRoute } = useFieldArray({
    control,
    name: `advanced.spec.ports.${portIndex}.routes` as const,
  });

  const dryRun = watch("dryRun.raw.value");
  const gvcName = watch("gvc.name");
  const routingMode = watch("advanced.spec.routingMode");
  const acceptAll = watch("advanced.spec.acceptAll");
  const routes = watch(`advanced.spec.ports.${portIndex}.routes`);

  const routeIndex = React.useMemo(() => {
    return routes.findIndex((r) => r.id === routeId);
  }, [portId, portIndex, routeId, routes]);

  const prefixType = watch(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.prefixType`) || "prefix";
  const hostType = watch(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.hostType`) || "prefix";
  const headers = watch(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.headers`) || [];
  const mirror = watch(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror`) || [];

  const hasDryRunError = getDryRunErrorText({
    dryRunResponse: (dryRun || {}) as any,
    path: `spec.ports[${portIndex}].routes[${routeIndex}]`,
    paths: [
      //
      `spec.ports[${portIndex}].routes[${routeIndex}].prefix`,
      `spec.ports[${portIndex}].routes[${routeIndex}].regex`,
      `spec.ports[${portIndex}].routes[${routeIndex}].replacePrefix`,
      `spec.ports[${portIndex}].routes[${routeIndex}].hostPrefix`,
      `spec.ports[${portIndex}].routes[${routeIndex}].hostRegex`,
      `spec.ports[${portIndex}].routes[${routeIndex}].port`,
      `spec.ports[${portIndex}].routes[${routeIndex}].replica`,
      `spec.ports[${portIndex}].routes[${routeIndex}].workloadLink`,
      `spec.ports[${portIndex}].routes[${routeIndex}].headers`,
      `spec.ports[${portIndex}].routes[${routeIndex}].headers.request`,
      `spec.ports[${portIndex}].routes[${routeIndex}].headers.request.set`,
      ...headers.map((h) => `spec.ports[${portIndex}].routes[${routeIndex}].headers.request.set.${h.name}`),
      `spec.ports[${portIndex}].routes[${routeIndex}].mirror`,
      ...mirror.flatMap((_, mi) => [
        `spec.ports[${portIndex}].routes[${routeIndex}].mirror[${mi}]`,
        `spec.ports[${portIndex}].routes[${routeIndex}].mirror[${mi}].workloadLink`,
        `spec.ports[${portIndex}].routes[${routeIndex}].mirror[${mi}].percent`,
      ]),
    ],
  });

  function handleRemoveRoute(id: string) {
    const routeIndexToRemove = routes.findIndex((r) => r.id === id);
    if (routeIndexToRemove >= 0) {
      removeRoute(routeIndexToRemove);
    }
  }

  // 1 type - all
  // 2 prefix/regex - all
  // 2 replacePrefix - all
  // 1 type (only when either acceptAllHosts or acceptAllSubdomains are enabled)
  // 2 hostPrefix/hostRegex (only when either acceptAllHosts or acceptAllSubdomains are enabled)
  // 2 workloadLink - pathBased
  // 1 port - all
  // 1 replica - workloadSubdomain

  const colCount = React.useMemo(() => {
    let val = 0;
    if (routingMode === "pathBased") {
      val = 8;
    }
    if (routingMode === "workloadSubdomain") {
      val = 7;
    }
    if (acceptAll !== "none") {
      val += 3;
    }
    return val;
  }, [routingMode, acceptAll]);

  return (
    <div key={`port-routes-${portId}-route-${routeId}`}>
      <div className="mb-1">
        <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}>
          <FormNGSelect
            name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.prefixType` as const}
            control={control}
            className="col-span-1"
            options={[
              { label: "Prefix", value: "prefix" },
              { label: "Regex", value: "regex" },
            ]}
          />
          {prefixType === "prefix" ? (
            <FormNGInput
              name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.prefix` as const}
              control={control}
              className="col-span-2"
            />
          ) : (
            <FormNGInput
              name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.regex` as const}
              control={control}
              className="col-span-2"
            />
          )}
          <FormNGInput
            name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.replacePrefix` as const}
            control={control}
            className="col-span-2"
          />
          {acceptAll !== "none" ? (
            <>
              <FormNGSelect
                name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.hostType` as const}
                control={control}
                className="col-span-1"
                options={[
                  { label: "Prefix", value: "prefix" },
                  { label: "Regex", value: "regex" },
                ]}
              />
              {hostType === "prefix" ? (
                <FormNGInput
                  name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.hostPrefix` as const}
                  control={control}
                  className="col-span-2"
                />
              ) : (
                <FormNGInput
                  name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.hostRegex` as const}
                  control={control}
                  className="col-span-2"
                />
              )}
            </>
          ) : null}
          {routingMode === "pathBased" ? (
            <FormNGKindSelect
              name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.workload.name` as const}
              control={control}
              rules={{ required: "Workload is required" }}
              key={gvcName}
              onChangeExtra={(value, item) => {
                const selfLink = linksOf(item).self!;
                setValue(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.workload.link`, selfLink);
                setValue(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.workload.item`, item);
              }}
              className="col-span-2"
              kind="workload"
              queries={[{ rel: "gvc", value: gvcName }]}
            />
          ) : null}
          {routingMode === "workloadSubdomain" ? (
            <>
              <FormNGInput
                name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.port` as const}
                control={control}
                className="col-span-1"
              />
              <div className="col-span-1 flex items-center gap-2">
                <FormNGInput
                  name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.replica` as const}
                  control={control}
                />
                <MinusCircleOutlined className="color-danger" onClick={() => handleRemoveRoute(routeId)} />
              </div>
            </>
          ) : (
            <div className="col-span-1 flex items-center gap-2">
              <FormNGInput
                name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.port` as const}
                control={control}
              />
              <MinusCircleOutlined className="color-danger" onClick={() => handleRemoveRoute(routeId)} />
            </div>
          )}
        </div>
        {hasDryRunError.message ? (
          <div className="mt-1 -mb-6">
            <FormDryRunAlert
              path={`spec.ports[${portIndex}].routes[${routeIndex}]`}
              paths={[
                `spec.ports[${portIndex}].routes[${routeIndex}].prefix`,
                `spec.ports[${portIndex}].routes[${routeIndex}].regex`,
                `spec.ports[${portIndex}].routes[${routeIndex}].replacePrefix`,
                `spec.ports[${portIndex}].routes[${routeIndex}].hostPrefix`,
                `spec.ports[${portIndex}].routes[${routeIndex}].hostRegex`,
                `spec.ports[${portIndex}].routes[${routeIndex}].port`,
                `spec.ports[${portIndex}].routes[${routeIndex}].replica`,
                `spec.ports[${portIndex}].routes[${routeIndex}].workloadLink`,
                `spec.ports[${portIndex}].routes[${routeIndex}].headers`,
                `spec.ports[${portIndex}].routes[${routeIndex}].headers.request`,
                `spec.ports[${portIndex}].routes[${routeIndex}].headers.request.set`,
                ...headers.map((h) => `spec.ports[${portIndex}].routes[${routeIndex}].headers.request.set.${h.name}`),
                `spec.ports[${portIndex}].routes[${routeIndex}].mirror`,
                ...mirror.flatMap((_, mi) => [
                  `spec.ports[${portIndex}].routes[${routeIndex}].mirror[${mi}]`,
                  `spec.ports[${portIndex}].routes[${routeIndex}].mirror[${mi}].workloadLink`,
                  `spec.ports[${portIndex}].routes[${routeIndex}].mirror[${mi}].percent`,
                ]),
              ]}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};
